import { useEffect, useMemo, useState } from 'react'
import { Settings } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { WorkspaceToggleChip } from '@/components/layout/workspace-scope-selector'
import { WorkspacePipGlyph } from '@/components/layout/workspace-pip-glyph'
import { ShowOverflowLink } from '@/components/ui/show-overflow-link'
import { useWorkspace } from '@/providers/workspace-provider'
import { useWorkspaceRecency } from '@/hooks/use-workspace-recency'
import {
  handleAdditiveWorkspaceScopeToggle,
  restoreAllWorkspaces,
  selectOnlyWorkspace,
} from '@/lib/workspace-scope-handlers'
import {
  LIST_OVERFLOW_PRIMARY_CAP,
  filterSidebarPickerWorkspaces,
  formatShowMoreWorkspacesLabel,
  orderWorkspacesForPicker,
  partitionListOverflow,
} from '@/lib/list-overflow'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import { SHELL_COLUMN_HEADER_CLASS, SHELL_ICON_RAIL_CLASS } from '@/lib/shell-chrome'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from '@/lib/menu-chrome'

function openWorkspaceSettings() {
  window.dispatchEvent(
    new CustomEvent('open-settings', { detail: { tab: 'organizations' } }),
  )
}

/** Global workspace scope control at the top of the left icon bar. */
export function SidebarWorkspaceScopeButton() {
  const { workspaces, activeWorkspaceIds, toggleWorkspace, setActiveWorkspaceIds } =
    useWorkspace()
  const { touchWorkspace, pipDisplay, orderIdsByRecency } =
    useWorkspaceRecency(activeWorkspaceIds)
  const [filter, setFilter] = useState('')
  const [open, setOpen] = useState(false)
  const [listExpanded, setListExpanded] = useState(false)
  /** Snapshot before Select all; undefined = not in select-all toggle mode. */
  const [preSelectAllIds, setPreSelectAllIds] = useState<string[] | undefined>(undefined)

  const pickerWorkspaces = useMemo(
    () => filterSidebarPickerWorkspaces(workspaces, activeWorkspaceIds),
    [workspaces, activeWorkspaceIds],
  )

  const allWorkspaceIds = useMemo(
    () => pickerWorkspaces.map((w) => w.id),
    [pickerWorkspaces],
  )
  const isAllSelected =
    allWorkspaceIds.length > 0 &&
    allWorkspaceIds.every((id) => activeWorkspaceIds.includes(id))
  const selectAllToggleActive = preSelectAllIds !== undefined && isAllSelected

  useEffect(() => {
    if (preSelectAllIds === undefined) return
    if (!isAllSelected) setPreSelectAllIds(undefined)
  }, [isAllSelected, preSelectAllIds])

  useEffect(() => {
    if (!open) {
      setListExpanded(false)
      setFilter('')
    }
  }, [open])

  const filteredList = useMemo(() => {
    const q = filter.trim().toLowerCase()
    const base = !q
      ? pickerWorkspaces
      : pickerWorkspaces.filter(
          (w) =>
            w.name.toLowerCase().includes(q) ||
            (w.slug?.toLowerCase().includes(q) ?? false),
        )
    return orderWorkspacesForPicker(base, activeWorkspaceIds, orderIdsByRecency)
  }, [filter, pickerWorkspaces, activeWorkspaceIds, orderIdsByRecency])

  const filterActive = filter.trim().length > 0
  const activeInFiltered = useMemo(
    () => filteredList.filter((w) => activeWorkspaceIds.includes(w.id)).length,
    [filteredList, activeWorkspaceIds],
  )
  const listPartition = useMemo(
    () =>
      partitionListOverflow(filteredList, {
        expanded: listExpanded,
        forceAll: filterActive,
        // Never bury a currently-active workspace under Show N more.
        cap: Math.max(LIST_OVERFLOW_PRIMARY_CAP, activeInFiltered),
      }),
    [filteredList, listExpanded, filterActive, activeInFiltered],
  )

  const showFilter = pickerWorkspaces.length > 8

  const handleToggle = (workspaceId: string) => {
    setPreSelectAllIds(undefined)
    touchWorkspace(workspaceId)
    handleAdditiveWorkspaceScopeToggle(
      workspaceId,
      activeWorkspaceIds,
      toggleWorkspace,
    )
  }

  const handleOnly = (workspaceId: string) => {
    setPreSelectAllIds(undefined)
    touchWorkspace(workspaceId)
    selectOnlyWorkspace(workspaceId, setActiveWorkspaceIds)
    setOpen(false)
    setFilter('')
  }

  const handleSelectAllToggle = () => {
    if (allWorkspaceIds.length === 0) return
    if (selectAllToggleActive) {
      setActiveWorkspaceIds(preSelectAllIds ?? allWorkspaceIds)
      setPreSelectAllIds(undefined)
      return
    }
    setPreSelectAllIds([...activeWorkspaceIds])
    setActiveWorkspaceIds([...allWorkspaceIds])
  }

  const emptyActive = activeWorkspaceIds.length === 0 && workspaces.length > 0

  useEffect(() => {
    const onTourScope = (event: Event) => {
      const detail = (event as CustomEvent<{ open?: boolean }>).detail
      if (typeof detail?.open !== 'boolean') return
      setOpen(detail.open)
      if (!detail.open) setFilter('')
    }
    window.addEventListener('flownotes:product-tour-workspace-scope', onTourScope)
    return () =>
      window.removeEventListener('flownotes:product-tour-workspace-scope', onTourScope)
  }, [])

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Active workspaces"
          aria-label="Workspace scope"
          data-testid={testIds.leftSidebar.workspaceScopeButton}
          className={cn(
            'group/pips flex flex-col items-center justify-center gap-0.5 py-0 overflow-hidden transition-colors',
            SHELL_ICON_RAIL_CLASS,
            SHELL_COLUMN_HEADER_CLASS,
            'text-sidebar-foreground/70 hover:bg-sidebar-accent/80 hover:text-sidebar-foreground',
            emptyActive && 'text-yellow-600 dark:text-yellow-500',
          )}
        >
          {emptyActive ? (
            <span className="text-[9px] font-semibold leading-none px-1 text-center">
              None
            </span>
          ) : (
            <WorkspacePipGlyph
              workspaces={workspaces}
              visibleIds={pipDisplay.visibleIds}
              overflow={pipDisplay.overflow}
              interactive
            />
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="right"
        align="start"
        className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
        collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
        data-testid={testIds.leftSidebar.workspaceScopePopover}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="mb-1.5 flex items-center justify-between gap-2 px-1.5">
          <p className="text-sm font-semibold text-foreground">Active workspaces</p>
          <button
            type="button"
            title="Manage workspaces"
            aria-label="Manage workspaces"
            onClick={() => {
              setOpen(false)
              openWorkspaceSettings()
            }}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            data-testid={testIds.leftSidebar.workspaceScopeManage}
          >
            <Settings className="h-3.5 w-3.5" />
          </button>
        </div>

        {emptyActive ? (
          <button
            type="button"
            onClick={() => {
              setPreSelectAllIds(undefined)
              restoreAllWorkspaces(pickerWorkspaces, setActiveWorkspaceIds)
            }}
            className="mb-1.5 w-full rounded px-2 py-1 text-center text-[11px] text-primary hover:bg-accent transition-colors"
            data-testid={testIds.leftSidebar.workspaceScopeRestoreAll}
          >
            Restore all workspaces
          </button>
        ) : null}

        {showFilter ? (
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter workspaces..."
            className="mb-1.5 h-7 text-xs"
            data-testid={testIds.leftSidebar.workspaceScopeFilter}
          />
        ) : null}

        <div className="flex flex-col gap-1 max-h-[min(50vh,320px)] overflow-y-auto">
          {listPartition.primary.map((w) => (
            <WorkspaceToggleChip
              key={w.id}
              workspace={w}
              active={activeWorkspaceIds.includes(w.id)}
              selectionMode="additive"
              onToggle={() => handleToggle(w.id)}
              onOnly={() => handleOnly(w.id)}
            />
          ))}
          <ShowOverflowLink
            expanded={listExpanded}
            overflowCount={listPartition.overflowCount}
            onToggle={() => setListExpanded((v) => !v)}
            showMoreLabel={formatShowMoreWorkspacesLabel(listPartition.overflowCount)}
            data-testid={testIds.leftSidebar.workspaceScopeShowMore}
          />
        </div>

        {filteredList.length === 0 && showFilter ? (
          <p className="px-2 py-2 text-[10px] text-muted-foreground text-center">
            No matching workspaces
          </p>
        ) : null}

        {!emptyActive && allWorkspaceIds.length > 1 ? (
          <button
            type="button"
            onClick={handleSelectAllToggle}
            aria-pressed={selectAllToggleActive}
            title={
              selectAllToggleActive
                ? 'Restore the workspace selection from before Select all'
                : 'Select every workspace'
            }
            data-testid={testIds.leftSidebar.workspaceScopeSelectAll}
            className={cn(
              'mt-1.5 w-full rounded px-2 py-1 text-center text-[11px] transition-colors duration-200 ease-in-out',
              selectAllToggleActive
                ? 'bg-accent text-accent-foreground'
                : 'text-foreground/80 hover:bg-accent hover:text-accent-foreground',
            )}
          >
            {selectAllToggleActive ? 'Restore previous' : 'Select all'}
          </button>
        ) : null}

      </DropdownMenuContent>
    </DropdownMenu>
  )
}
